import React, { useState } from "react";
import { CHART_1, INK, MUTED, GRID, PAPER } from "./palette";

// ---- bf_robustness: BF₀₁ across the Cauchy prior's scale r ----
// Props from ChartData#bf_robustness: points [[r, BF₀₁]] from r = 0 (where BF₀₁ = 1) upward, and
// marked [{ name, r, bf01 }] (BayesFactor's medium, wide and ultrawide presets).
// Tokens, axes and tooltips follow ConceptChart.jsx; teal is the curve being taught.
const W = 640, TOP = 16, RIGHT = 16;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
// A prior scale without trailing zeros: 0.707, 1, 1.414.
const fmtScale = (r) => String(+r.toFixed(3));
const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;
// Reference lines from the Bayes factor's benchmarks (Lee & Wagenmakers): 1 is no evidence either way.
const LINES = [{ at: 1, label: "1: no evidence either way" }, { at: 3, label: "Above 3: moderate for H₀" }, { at: 10, label: "Above 10: strong for H₀" }];

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(150, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function BfRobustnessChart({ points = [], marked = [] }) {
	const [hover, setHover] = useState(null);
	if (points.length < 2) return null;
	const height = 270, box = { l: 40, r: W - RIGHT - 150, t: TOP + 22, b: height - 40 };
	const rMax = points[points.length - 1][0];
	const top = Math.max(...points.map((p) => p[1]));
	const yStep = top > 12 ? 5 : top > 6 ? 2 : 1, yMax = Math.ceil((top * 1.05) / yStep) * yStep;
	const x = (v) => box.l + (v / rMax) * (box.r - box.l);
	const y = (b) => box.b - (b / yMax) * (box.b - box.t);
	const path = points.map(([r, b], i) => `${i ? "L" : "M"}${x(r).toFixed(1)},${y(b).toFixed(1)}`).join(" ");
	const yTicks = Array.from({ length: Math.round(yMax / yStep) + 1 }, (_, i) => i * yStep);
	const xTicks = Array.from({ length: Math.floor(rMax / 0.25 + 1e-9) + 1 }, (_, i) => i * 0.25);
	// Where BF₀₁ first reaches 3, interpolated, for the caption.
	const k = points.findIndex(([, b]) => b >= 3);
	const cross = k > 0 ? points[k - 1][0] + ((3 - points[k - 1][1]) / (points[k][1] - points[k - 1][1])) * (points[k][0] - points[k - 1][0]) : null;
	const bfs = marked.map((m) => m.bf01);
	const onMove = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const v = ((((event.clientX - rect.left) / rect.width) * W - box.l) / (box.r - box.l)) * rMax;
		if (v < 0 || v > rMax) return setHover(null);
		setHover(points.reduce((best, p) => (Math.abs(p[0] - v) < Math.abs(best[0] - v) ? p : best)));
	};
	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: CHART_1 }} />BF₀₁ with a Cauchy prior of scale r</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]" style={{ borderColor: INK }} />R's BayesFactor presets</span>
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
				aria-label={`BF₀₁ against the prior scale r, from 0 to ${fmt(rMax, 1)}. At r = 0 it is 1, and it rises as the prior widens.${marked.map((m) => ` ${upperFirst(m.name)}, r = ${fmtScale(m.r)}: BF₀₁ = ${fmt(m.bf01)}.`).join("")}`}>
				{yTicks.map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{xTicks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">r, the prior's scale (wider →)</text>
				<text x={box.l} y={box.t - 10} fontSize="11" fill={MUTED}>BF₀₁, evidence for no difference</text>
				{LINES.filter((l) => l.at <= yMax).map((l) => (
					<g key={l.at}>
						<line x1={box.l} x2={box.r} y1={y(l.at)} y2={y(l.at)} stroke={MUTED} strokeDasharray="4 3" />
						<text x={box.r + 6} y={y(l.at)} fontSize="11" fill={MUTED} dominantBaseline="middle">{l.label}</text>
					</g>
				))}
				<path d={path} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				{marked.map((m) => (
					<g key={m.name}>
						<circle cx={x(m.r)} cy={y(m.bf01)} r="4.5" fill={PAPER} stroke={INK} strokeWidth="1.5" />
						<Halo x={x(m.r) - 8} y={y(m.bf01) - 8} textAnchor="end" fill={INK} fontWeight="600">{upperFirst(m.name)}, r = {fmtScale(m.r)}: {fmt(m.bf01)}</Halo>
					</g>
				))}
				{hover && (
					<g pointerEvents="none">
						<circle cx={x(hover[0])} cy={y(hover[1])} r="3" fill={CHART_1} />
						<Tooltip x={x(hover[0])} lines={[`Prior scale r = ${fmt(hover[0])}`, `BF₀₁ = ${fmt(hover[1])}`]} />
					</g>
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each point is the Bayes factor this study would give with a different prior scale. At r = 0 the alternative becomes the null, so BF₀₁ = 1.
				Wider priors bet on bigger differences, which our small one fits worse, so the evidence for no difference grows.
				{bfs.length > 1 && ` Across R's ${WORDS[marked.length] || marked.length} presets it runs from ${fmt(Math.min(...bfs))} to ${fmt(Math.max(...bfs))}, on the null's side every time.`}
				{cross != null && ` Only priors narrower than r = ${fmt(cross)} would put it below 3.`}
			</figcaption>
		</figure>
	);
}
